import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Search, Plus, Lightbulb } from 'lucide-react';

const NoResultsWithAction = ({
  searchQuery,
  selectedCategory,
  onCreateProduct,
  showCreateButton = true,
  clientName = ''
}) => {
  const handleCreateProduct = () => {
    // Pre-populate with search context
    const initialData = {
      name: '',  // Keep name empty by default
      category: selectedCategory || '',
      searchContext: {
        query: searchQuery,
        category: selectedCategory,
        clientName: clientName
      }
    };
    onCreateProduct(initialData);
  };

  // Determine the primary message based on context
  const getPrimaryMessage = () => {
    if (selectedCategory && searchQuery) {
      return `在分类"${selectedCategory}"中未找到"${searchQuery}"`;
    } else if (selectedCategory) {
      return `分类"${selectedCategory}"暂无产品`;
    } else if (searchQuery) {
      return `未找到"${searchQuery}"的相关产品`;
    } else {
      return '未找到匹配的产品';
    }
  };

  const getSecondaryMessage = () => {
    if (searchQuery && showCreateButton) {
      return '您可以基于当前搜索内容创建新产品';
    } else {
      return '请尝试调整搜索关键词或筛选条件';
    }
  };

  const getSuggestions = () => {
    const suggestions = [];
    if (searchQuery) {
      suggestions.push('尝试使用更少或更通用的关键词');
      suggestions.push('检查拼写是否正确');
    }
    if (selectedCategory) {
      suggestions.push('选择其他产品分类');
    }
    if (!searchQuery && !selectedCategory) {
      suggestions.push('输入产品名称或编号进行搜索');
      suggestions.push('从左侧分类中选择产品类别');
    }
    return suggestions;
  };

  return (
    <div className="text-center py-12 px-6 flex flex-col items-center gap-6">
      {/* Main Icon */}
      <div className="relative flex items-center justify-center">
        <Search className="h-16 w-16 text-muted-foreground/50" />
        {showCreateButton && (
          <Plus className="h-6 w-6 text-primary absolute -bottom-1 -right-1 bg-background rounded-full border-2 border-primary" />
        )}
      </div>

      {/* Primary Message */}
      <div>
        <h6 className="text-lg text-muted-foreground mb-1">
          {getPrimaryMessage()}
        </h6>
        <p className="text-sm text-muted-foreground">
          {getSecondaryMessage()}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-4 items-center max-w-[360px]">
        {showCreateButton && searchQuery && (
          <Button
            size="lg"
            onClick={handleCreateProduct}
            className="min-w-[200px] py-6 text-base font-semibold shadow-md hover:shadow-lg hover:-translate-y-px transition-all"
          >
            <Plus className="mr-2 h-5 w-5" />
            创建产品"{searchQuery}"
          </Button>
        )}

        {/* Suggestions Panel */}
        {getSuggestions().length > 0 && (
          <Card className="bg-primary/10 border-primary/20 w-full max-w-[400px]">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="h-5 w-5 text-primary" />
                <span className="text-sm font-bold text-primary">搜索建议</span>
              </div>
              <ul className="pl-4 m-0 space-y-1">
                {getSuggestions().map((suggestion, index) => (
                  <li key={index} className="text-sm text-primary leading-relaxed">
                    {suggestion}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Context Information */}
      {(searchQuery || selectedCategory || clientName) && (
        <div className="mt-4 p-4 bg-muted rounded-md max-w-[500px] w-full">
          <span className="text-xs text-muted-foreground block mb-2">
            当前搜索上下文:
          </span>
          <div className="flex flex-wrap gap-2">
            {searchQuery && (
              <span className="px-3 py-1 bg-primary/10 border border-primary/30 rounded text-xs text-primary">
                搜索: "{searchQuery}"
              </span>
            )}
            {selectedCategory && (
              <span className="px-3 py-1 bg-secondary/10 border border-secondary/30 rounded text-xs text-secondary-foreground">
                分类: {selectedCategory}
              </span>
            )}
            {clientName && (
              <span className="px-3 py-1 bg-success/10 border border-success/20 rounded text-xs text-success">
                客户: {clientName}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NoResultsWithAction;
